/**
 * Public multi-step forgot-password page.
 * Steps: email → OTP → new password → success.
 * resetToken is held in component state only (not localStorage).
 */

import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  requestPasswordResetOtp,
  resetPasswordWithToken,
  verifyPasswordResetOtp,
} from "@/api/passwordResetApi";

const PASSWORD_MIN = 8;
const PASSWORD_MAX = 128;
const RESEND_COOLDOWN_SEC = 60;

function PageShell({ children }) {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white px-4 py-10 dark:from-slate-950 dark:to-slate-900">
      <div className="mx-auto w-full max-w-md">
        <div className="mb-8 text-center">
          <span className="text-lg font-bold tracking-tight text-teal-700 dark:text-teal-400">
            SentroDesk
          </span>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8 dark:border-slate-700 dark:bg-slate-900/90 dark:shadow-none">
          {children}
        </div>
        <p className="mt-6 text-center text-xs text-slate-400">
          <Link
            to="/login"
            className="font-medium text-teal-700 hover:underline dark:text-teal-400"
          >
            Back to sign in
          </Link>
        </p>
      </div>
    </div>
  );
}

const fieldClassName =
  "w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20 dark:border-slate-600 dark:bg-slate-950 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-teal-500 dark:focus:ring-teal-900/50";

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  /** @type {["email"|"otp"|"password"|"success", function]} */
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const id = window.setInterval(() => {
      setResendIn((v) => (v <= 1 ? 0 : v - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [resendIn]);

  const startResendCooldown = useCallback(() => {
    setResendIn(RESEND_COOLDOWN_SEC);
  }, []);

  const handleRequestOtp = useCallback(
    async (e) => {
      e.preventDefault();
      setError("");
      const trimmed = email.trim().toLowerCase();
      if (!trimmed || !trimmed.includes("@")) {
        setError("Enter a valid email address.");
        return;
      }
      setLoading(true);
      try {
        await requestPasswordResetOtp(trimmed);
        setEmail(trimmed);
        setStep("otp");
        startResendCooldown();
      } catch (err) {
        setError(err instanceof Error ? err.message : "Could not send reset code.");
      } finally {
        setLoading(false);
      }
    },
    [email, startResendCooldown],
  );

  const handleResend = useCallback(async () => {
    if (resendIn > 0 || loading) return;
    setError("");
    setLoading(true);
    try {
      await requestPasswordResetOtp(email);
      startResendCooldown();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not resend code.");
    } finally {
      setLoading(false);
    }
  }, [email, loading, resendIn, startResendCooldown]);

  const handleVerifyOtp = useCallback(
    async (e) => {
      e.preventDefault();
      setError("");
      const code = otp.trim();
      if (!code) {
        setError("Enter the code from your email.");
        return;
      }
      setLoading(true);
      try {
        const { resetToken: token } = await verifyPasswordResetOtp({ email, otp: code });
        if (!token) throw new Error("Verification failed.");
        setResetToken(token);
        setStep("password");
      } catch (err) {
        setError(err instanceof Error ? err.message : "Invalid or expired code.");
      } finally {
        setLoading(false);
      }
    },
    [email, otp],
  );

  const handleResetPassword = useCallback(
    async (e) => {
      e.preventDefault();
      setError("");
      if (password.length < PASSWORD_MIN) {
        setError(`Password must be at least ${PASSWORD_MIN} characters.`);
        return;
      }
      if (password.length > PASSWORD_MAX) {
        setError(`Password must not exceed ${PASSWORD_MAX} characters.`);
        return;
      }
      if (password !== confirm) {
        setError("Passwords do not match.");
        return;
      }
      if (!resetToken) {
        setError("Session expired. Please start again.");
        setStep("email");
        return;
      }
      setLoading(true);
      try {
        await resetPasswordWithToken({ resetToken, newPassword: password });
        setResetToken("");
        setStep("success");
      } catch (err) {
        setError(err instanceof Error ? err.message : "Password update failed.");
      } finally {
        setLoading(false);
      }
    },
    [password, confirm, resetToken],
  );

  if (step === "success") {
    return (
      <PageShell>
        <div className="flex flex-col items-center py-6 text-center">
          <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
            <i className="ri-checkbox-circle-line text-2xl" />
          </span>
          <h1 className="text-lg font-semibold text-slate-900">Password updated</h1>
          <p className="mt-2 text-sm text-slate-500">
            You can now sign in with your new password.
          </p>
          <button
            type="button"
            className="mt-6 inline-flex items-center justify-center gap-2 rounded-xl bg-teal-600 px-6 py-2.5 text-sm font-semibold text-white shadow transition hover:bg-teal-700"
            onClick={() => navigate("/login", { replace: true })}
          >
            Go to login
            <i className="ri-arrow-right-line text-base" />
          </button>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      {step === "email" ? (
        <>
          <h1 className="text-xl font-bold tracking-tight text-slate-900">Reset your password</h1>
          <p className="mt-2 text-sm text-slate-500">
            Enter the email for your SentroDesk account. If it exists, we will send a one-time code.
          </p>
          <form onSubmit={handleRequestOtp} className="mt-6 space-y-4" noValidate>
            {error ? (
              <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700" role="alert">
                {error}
              </div>
            ) : null}
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-slate-700" htmlFor="fp-email">
                Email
              </label>
              <input
                id="fp-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                className={fieldClassName}
                placeholder="you@company.com"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-teal-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-teal-700 disabled:opacity-60"
            >
              {loading ? <i className="ri-loader-4-line animate-spin" /> : null}
              Continue
            </button>
          </form>
        </>
      ) : null}

      {step === "otp" ? (
        <>
          <h1 className="text-xl font-bold tracking-tight text-slate-900">Enter verification code</h1>
          <p className="mt-2 text-sm text-slate-500">
            If an account exists for <span className="font-medium text-slate-700">{email}</span>, we
            sent a code. Enter it below.
          </p>
          <form onSubmit={handleVerifyOtp} className="mt-6 space-y-4" noValidate>
            {error ? (
              <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700" role="alert">
                {error}
              </div>
            ) : null}
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-slate-700" htmlFor="fp-otp">
                One-time code
              </label>
              <input
                id="fp-otp"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                disabled={loading}
                className={`${fieldClassName} text-center text-lg font-semibold tracking-[0.35em]`}
                placeholder="••••••"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-teal-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-teal-700 disabled:opacity-60"
            >
              {loading ? <i className="ri-loader-4-line animate-spin" /> : null}
              Verify code
            </button>
            <button
              type="button"
              disabled={loading || resendIn > 0}
              onClick={() => void handleResend()}
              className="w-full text-center text-xs font-semibold text-teal-700 disabled:text-slate-400"
            >
              {resendIn > 0 ? `Resend code in ${resendIn}s` : "Resend code"}
            </button>
          </form>
        </>
      ) : null}

      {step === "password" ? (
        <>
          <h1 className="text-xl font-bold tracking-tight text-slate-900">Choose a new password</h1>
          <p className="mt-2 text-sm text-slate-500">
            Use at least {PASSWORD_MIN} characters. You will use this password to sign in.
          </p>
          <form onSubmit={handleResetPassword} className="mt-6 space-y-4" noValidate>
            {error ? (
              <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700" role="alert">
                {error}
              </div>
            ) : null}
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-slate-700" htmlFor="fp-pass">
                New password
              </label>
              <div className="relative">
                <input
                  id="fp-pass"
                  type={showPass ? "text" : "password"}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  className={`${fieldClassName} pr-10`}
                  required
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500"
                  onClick={() => setShowPass((v) => !v)}
                  aria-label={showPass ? "Hide password" : "Show password"}
                >
                  <i className={showPass ? "ri-eye-off-line" : "ri-eye-line"} />
                </button>
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-slate-700" htmlFor="fp-confirm">
                Confirm password
              </label>
              <input
                id="fp-confirm"
                type={showPass ? "text" : "password"}
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                disabled={loading}
                className={fieldClassName}
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-teal-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-teal-700 disabled:opacity-60"
            >
              {loading ? <i className="ri-loader-4-line animate-spin" /> : null}
              Update password
            </button>
          </form>
        </>
      ) : null}
    </PageShell>
  );
}
